"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

import { fetchJson } from "@/lib/api";
import { useI18n } from "@/components/useI18n";
import { OAuthProviderLogo } from "@/components/auth/OAuthProviderLogo";

type VersionInfo = {
  gitSha: string;
  buildTime?: string | null;
};

export function LoginClient({ version, nextPath = "", initialError = "" }: { version?: VersionInfo; nextPath?: string; initialError?: string }) {
  const { t } = useI18n();
  const [identifier, setIdentifier] = useState<string>("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(initialError || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      const data = await fetchJson<{ authenticated: boolean }>("/api/auth/status", { cache: "no-store" });
      if (data.authenticated) {
        window.location.href = "/app/all/home";
        return;
      }
    })();
  }, []);

  async function submit(form?: HTMLFormElement | null) {
    const formData = form ? new FormData(form) : null;
    const nextIdentifier = String(formData?.get("identifier") ?? identifier);
    const nextPassword = String(formData?.get("password") ?? password);
    setLoading(true);
    setMessage(null);
    try {
      await fetchJson<{ ok: true }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(nextIdentifier.includes("@") ? { email: nextIdentifier, password: nextPassword } : { member_global_id: nextIdentifier, password: nextPassword }),
      });
      window.location.href = nextPath || "/app/all/home";
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "ログインに失敗しました");
    } finally {
      setLoading(false);
    }
  }
  void submit;

  return (
    <div className="mx-auto grid w-full max-w-xl gap-6">
      <div data-testid="login-panel" className="surface-panel mx-auto w-full max-w-xl rounded-md p-6">
        <div className="mb-2 text-xs font-semibold uppercase text-[color:var(--muted)]">Tairoh</div>
        <h2 className="mb-2 text-xl font-semibold">{t("login")}</h2>
        <p className="mb-5 text-sm text-[color:var(--muted)]">メールアドレス、またはメンバーIDでログインします。</p>
        {message ? <div className="mb-3 text-sm text-[color:var(--danger)]">{message}</div> : null}
        <form
          data-testid="login-form"
          data-ready="true"
          action={`/api/auth/login-form${nextPath ? `?next=${encodeURIComponent(nextPath)}` : ""}`}
          method="post"
        >
          <label className="grid gap-1 text-sm">
            <span className="text-[color:var(--muted)]">メールアドレス / メンバーID</span>
            <input
              data-testid="login-identifier"
              name="identifier"
              className="field-input rounded-md"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="you@example.com"
              autoComplete="username"
            />
          </label>
          <label className="mt-3 grid gap-1 text-sm">
            <span className="text-[color:var(--muted)]">{t("password")}</span>
            <input
              data-testid="login-password"
              name="password"
              type="password"
              className="field-input rounded-md"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          <button
            data-testid="login-submit"
            type="submit"
            className="button-primary mt-4 h-10 w-full rounded-md text-sm font-semibold"
            disabled={loading}
          >
            {t("login")}
          </button>
        </form>
        <div className="mt-4 grid gap-2">
          <a className="button-secondary inline-flex h-10 items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold" href={`/api/auth/oauth/google/start?next=${encodeURIComponent(nextPath || "/app/all/home")}`}><OAuthProviderLogo provider="google" /><span>Googleでログイン</span></a>
          <a className="button-secondary inline-flex h-10 items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold" href={`/api/auth/oauth/microsoft/start?next=${encodeURIComponent(nextPath || "/app/all/home")}`}><OAuthProviderLogo provider="microsoft" /><span>Microsoftでログイン</span></a>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-[color:var(--muted)]">アカウントがない場合</span>
          <Link href="/signup" className="font-semibold text-[color:var(--accent-strong)]">{t("accountCreate")}</Link>
        </div>
        <div className="mt-3 text-center text-sm"><Link href={`/password-reset${nextPath ? `?next=${encodeURIComponent(nextPath)}` : ""}`} className="font-semibold text-[color:var(--accent-strong)]">パスワードを忘れた場合</Link></div>
        {version ? (
          <div className="mt-3 text-[11px] text-[color:var(--muted)]">
            version: <span className="font-mono">{version.gitSha.slice(0, 12)}</span>
            {version.buildTime ? (
              <>
                {" "}
                (<span className="font-mono">{version.buildTime}</span>)
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

